import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import BuildingDetailsModal from "./BuildingDetailsModal";
import "./StatsBuildingsAgeStatus.css";

const AGE_BANDS = ["Under 50", "50-99", "100-149", "150+", "Unknown"];

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toISOString().slice(0, 10);
};

const exportCsv = (filename, headers, rows) => {
    const escape = (value) => {
        const text = String(value ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const statusColor = (status) => {
    const normal = String(status || "Unknown").toLowerCase();
    if (normal.includes("inactive")) return "rgba(148, 163, 184, 0.72)";
    if (normal.includes("active")) return "rgba(20, 184, 166, 0.74)";
    if (normal.includes("demolished")) return "rgba(244, 63, 94, 0.68)";
    if (normal.includes("sold")) return "rgba(245, 158, 11, 0.72)";
    return "rgba(99, 102, 241, 0.66)";
};

function StatsBuildingsAgeStatus({ onClose }) {
    const [buildings, setBuildings] = useState([]);
    const [cities, setCities] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [showNeedsAttention, setShowNeedsAttention] = useState(false);
    const [editingBuildingId, setEditingBuildingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const ageChartRef = useRef(null);
    const cityChartRef = useRef(null);
    const completenessChartRef = useRef(null);
    const ageChartInstance = useRef(null);
    const cityChartInstance = useRef(null);
    const completenessChartInstance = useRef(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                setError(null);
                const { data } = await axios.get("/api/v1/buildings/stats/age");
                setBuildings(data.buildings || []);
                setCities(data.cities || []);
                setStatuses(data.statuses || []);
            } catch (err) {
                console.error("Error fetching building statistics:", err);
                setError("Failed to load building statistics.");
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const filteredBuildings = useMemo(() => {
        return buildings.filter((building) => {
            const needsAttention = !building.established_date ||
                !building.city ||
                !building.status ||
                !building.has_coordinates ||
                !building.has_images ||
                !building.has_documents;
            return (!selectedCity || building.city === selectedCity) &&
                (!selectedStatus || building.status === selectedStatus) &&
                (!showNeedsAttention || needsAttention);
        });
    }, [buildings, selectedCity, selectedStatus, showNeedsAttention]);

    const summary = useMemo(() => {
        const total = filteredBuildings.length;
        const active = filteredBuildings.filter((building) => String(building.status || "").toLowerCase() === "active").length;
        const historic = filteredBuildings.filter((building) => building.historical_status).length;
        const mapped = filteredBuildings.filter((building) => building.has_coordinates).length;
        const withImages = filteredBuildings.filter((building) => building.has_images).length;
        const withDocuments = filteredBuildings.filter((building) => building.has_documents).length;
        const averageAge = filteredBuildings
            .filter((building) => Number.isFinite(Number(building.age_years)))
            .reduce((acc, building, _index, source) => acc + Number(building.age_years) / source.length, 0);
        return { total, active, historic, mapped, withImages, withDocuments, averageAge: averageAge ? Math.round(averageAge) : 0 };
    }, [filteredBuildings]);

    const needsAttentionRows = useMemo(() => {
        return filteredBuildings
            .map((building) => {
                const missing = [
                    !building.established_date && "date",
                    !building.city && "city",
                    !building.status && "status",
                    !building.has_coordinates && "map",
                    !building.has_images && "image",
                    !building.has_documents && "document",
                ].filter(Boolean);
                return { ...building, missing };
            })
            .filter((building) => building.missing.length)
            .sort((a, b) => b.missing.length - a.missing.length || String(a.name).localeCompare(String(b.name)));
    }, [filteredBuildings]);

    const ageBandMatrix = useMemo(() => {
        const statusList = [...new Set(filteredBuildings.map((building) => building.status || "Unknown"))].sort();
        const matrix = {};
        AGE_BANDS.forEach((band) => {
            matrix[band] = {};
            statusList.forEach((status) => {
                matrix[band][status] = 0;
            });
        });
        filteredBuildings.forEach((building) => {
            const band = AGE_BANDS.includes(building.age_band) ? building.age_band : "Unknown";
            const status = building.status || "Unknown";
            matrix[band][status] = (matrix[band][status] || 0) + 1;
        });
        return { statusList, matrix };
    }, [filteredBuildings]);

    const cityRows = useMemo(() => {
        const counts = {};
        filteredBuildings.forEach((building) => {
            const city = building.city || "Unknown";
            counts[city] = (counts[city] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([city, count]) => ({ city, count }))
            .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city))
            .slice(0, 12);
    }, [filteredBuildings]);

    const oldestBuildings = useMemo(() => {
        return [...filteredBuildings]
            .filter((building) => building.established_date)
            .sort((a, b) => new Date(a.established_date) - new Date(b.established_date))
            .slice(0, 10);
    }, [filteredBuildings]);

    useEffect(() => {
        if (!ageChartRef.current || loading || error) return;
        if (ageChartInstance.current) ageChartInstance.current.destroy();

        ageChartInstance.current = new Chart(ageChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: AGE_BANDS,
                datasets: ageBandMatrix.statusList.map((status) => ({
                    label: status,
                    data: AGE_BANDS.map((band) => ageBandMatrix.matrix[band]?.[status] || 0),
                    backgroundColor: statusColor(status),
                    stack: "age-status",
                })),
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { stacked: true, beginAtZero: true, title: { display: true, text: "Buildings" } },
                    y: { stacked: true },
                },
                plugins: {
                    legend: { position: "top" },
                    title: { display: true, text: "Building Age Bands by Current Status" },
                },
            },
        });

        return () => {
            if (ageChartInstance.current) ageChartInstance.current.destroy();
        };
    }, [ageBandMatrix, error, loading]);

    useEffect(() => {
        if (!cityChartRef.current || loading || error) return;
        if (cityChartInstance.current) cityChartInstance.current.destroy();

        cityChartInstance.current = new Chart(cityChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: cityRows.map((row) => row.city),
                datasets: [{
                    label: "Buildings",
                    data: cityRows.map((row) => row.count),
                    backgroundColor: "rgba(37, 99, 235, 0.68)",
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0 } },
                    x: { ticks: { maxRotation: 40, minRotation: 20 } },
                },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Top Cities by Building Count" },
                },
            },
        });

        return () => {
            if (cityChartInstance.current) cityChartInstance.current.destroy();
        };
    }, [cityRows, error, loading]);

    useEffect(() => {
        if (!completenessChartRef.current || loading || error) return;
        if (completenessChartInstance.current) completenessChartInstance.current.destroy();

        const total = summary.total || 1;
        const rows = [
            ["Mapped", summary.mapped],
            ["Images", summary.withImages],
            ["Documents", summary.withDocuments],
            ["Historic Flag", summary.historic],
            ["Active", summary.active],
        ];

        completenessChartInstance.current = new Chart(completenessChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: rows.map(([label]) => label),
                datasets: [{
                    label: "% of filtered buildings",
                    data: rows.map(([, value]) => Math.round((value / total) * 100)),
                    backgroundColor: [
                        "rgba(20, 184, 166, 0.72)",
                        "rgba(245, 158, 11, 0.72)",
                        "rgba(99, 102, 241, 0.68)",
                        "rgba(14, 165, 233, 0.68)",
                        "rgba(34, 197, 94, 0.68)",
                    ],
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { beginAtZero: true, max: 100, ticks: { callback: (value) => `${value}%` } },
                },
                plugins: {
                    legend: { display: false },
                    title: { display: true, text: "Record Completeness" },
                },
            },
        });

        return () => {
            if (completenessChartInstance.current) completenessChartInstance.current.destroy();
        };
    }, [error, loading, summary]);

    const exportRegister = () => {
        exportCsv(
            "building-statistics-register.csv",
            ["Name", "City", "Status", "Age Band", "Established", "Age", "Capacity", "Historic", "Mapped", "Images", "Documents", "Ownership"],
            filteredBuildings.map((building) => [
                building.name,
                building.city || "",
                building.status || "",
                building.age_band || "Unknown",
                formatDate(building.established_date),
                building.age_years || "",
                building.capacity || "",
                building.historical_status ? "Yes" : "No",
                building.has_coordinates ? "Yes" : "No",
                building.image_count || 0,
                building.document_count || 0,
                building.ownership_status || "",
            ])
        );
    };

    const exportAttention = () => {
        exportCsv(
            "building-records-needing-attention.csv",
            ["Name", "City", "Status", "Missing"],
            needsAttentionRows.map((building) => [
                building.name,
                building.city || "",
                building.status || "",
                building.missing.join(", "),
            ])
        );
    };

    const exportChart = (chart, filename) => {
        if (!chart.current) return;
        const link = document.createElement("a");
        link.href = chart.current.toBase64Image();
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className="stats-buildings-age-overlay">
            <div className="stats-buildings-age-content">
                <header className="stats-buildings-age-header">
                    <div>
                        <p className="bstat-eyebrow">Estate Intelligence</p>
                        <h2>Building Statistics</h2>
                    </div>
                    <button className="bstat-close-button" onClick={onClose} aria-label="Close Building Statistics">&times;</button>
                </header>

                <div className="stats-buildings-age-body">
                    {loading && <div className="loading-spinner">Loading stats...</div>}
                    {error && <div className="error-message">{error}</div>}

                    {!loading && !error && (
                        <>
                            <section className="bstat-toolbar">
                                <label>
                                    City
                                    <select value={selectedCity} onChange={(event) => setSelectedCity(event.target.value)}>
                                        <option value="">All cities</option>
                                        {cities.map((city) => <option key={city} value={city}>{city}</option>)}
                                    </select>
                                </label>
                                <label>
                                    Status
                                    <select value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)}>
                                        <option value="">All statuses</option>
                                        {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                </label>
                                <label className="bstat-check">
                                    <input
                                        type="checkbox"
                                        checked={showNeedsAttention}
                                        onChange={(event) => setShowNeedsAttention(event.target.checked)}
                                    />
                                    Needs attention only
                                </label>
                                <button className="bstat-primary-btn" onClick={exportRegister}>Export Register</button>
                                <button className="bstat-secondary-btn" onClick={exportAttention}>Export Gaps</button>
                            </section>

                            <section className="bstat-kpi-grid">
                                <Kpi label="Buildings" value={summary.total} />
                                <Kpi label="Active" value={summary.active} />
                                <Kpi label="Historic" value={summary.historic} />
                                <Kpi label="Mapped" value={`${summary.mapped}/${summary.total}`} />
                                <Kpi label="Average Age" value={summary.averageAge ? `${summary.averageAge} yrs` : "Unknown"} />
                                <Kpi label="Needs Attention" value={needsAttentionRows.length} />
                            </section>

                            <section className="bstat-grid-two">
                                <Panel title="Age and Status Profile" action={<button onClick={() => exportChart(ageChartInstance, "building-age-status.png")}>PNG</button>}>
                                    <div className="bstat-chart">
                                        <canvas ref={ageChartRef}></canvas>
                                    </div>
                                </Panel>
                                <Panel title="Record Completeness" action={<button onClick={() => exportChart(completenessChartInstance, "building-record-completeness.png")}>PNG</button>}>
                                    <div className="bstat-chart">
                                        <canvas ref={completenessChartRef}></canvas>
                                    </div>
                                </Panel>
                            </section>

                            <section className="bstat-grid-two">
                                <Panel title="City Spread" action={<button onClick={() => exportChart(cityChartInstance, "building-city-spread.png")}>PNG</button>}>
                                    <div className="bstat-chart bstat-chart-short">
                                        <canvas ref={cityChartRef}></canvas>
                                    </div>
                                </Panel>
                                <Panel title="Oldest Buildings">
                                    <BuildingTable
                                        headers={["Established", "Building", "City", "Status", "Age"]}
                                        rows={oldestBuildings.map((building) => [
                                            formatDate(building.established_date),
                                            building.name,
                                            building.city || "",
                                            building.status || "",
                                            building.age_years ? `${building.age_years}` : "",
                                        ])}
                                        empty="No dated buildings found."
                                    />
                                </Panel>
                            </section>

                            <Panel title="Records Needing Attention" action={<span>{needsAttentionRows.length} records</span>}>
                                <BuildingTable
                                    headers={["Building", "City", "Status", "Missing", "Update"]}
                                    rows={needsAttentionRows.slice(0, 30).map((building) => [
                                        building.name,
                                        building.city || "",
                                        building.status || "",
                                        building.missing.join(", "),
                                        <button className="bstat-link-btn" onClick={() => setEditingBuildingId(building.id)}>Update</button>,
                                    ])}
                                    empty="All filtered records have the core fields."
                                />
                            </Panel>

                            <Panel title="Building Register" action={<span>{filteredBuildings.length} shown</span>}>
                                <BuildingTable
                                    headers={["Building", "City", "Status", "Established", "Age Band", "Images", "Documents", "Mapped"]}
                                    rows={filteredBuildings.map((building) => [
                                        building.name,
                                        building.city || "",
                                        building.status || "",
                                        formatDate(building.established_date),
                                        building.age_band || "Unknown",
                                        building.image_count || 0,
                                        building.document_count || 0,
                                        building.has_coordinates ? "Yes" : "No",
                                    ])}
                                    empty="No buildings match these filters."
                                />
                            </Panel>
                        </>
                    )}
                </div>
                {editingBuildingId && (
                    <BuildingDetailsModal
                        initialBuildingId={editingBuildingId}
                        onClose={() => setEditingBuildingId(null)}
                    />
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="bstat-kpi">
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );
}

function Panel({ title, action, children }) {
    return (
        <section className="bstat-panel">
            <div className="bstat-panel-heading">
                <h3>{title}</h3>
                {action && <div className="bstat-panel-action">{action}</div>}
            </div>
            {children}
        </section>
    );
}

function BuildingTable({ headers, rows, empty }) {
    return (
        <div className="bstat-table-wrap">
            <table className="bstat-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row[0]}`}>
                        {row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr>
                        <td colSpan={headers.length}>{empty}</td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default StatsBuildingsAgeStatus;
